import { useMutation } from '@apollo/client';
import { CancelButton } from 'components/common/DefaultFormButton';
import DefaultModal from 'components/common/DefaultModal';
import LocationSelectForm from 'components/common/LocationSelectForm';
import SweetAlertAsModal from 'components/common/SweetAlertAsModal';
import { ERRORS } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import { useManyDataQuery } from 'hooks/useFastGqlQuery';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { AuthAction } from 'redux/actions';
import ROUTES from 'routes/routeConstants';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GET_COMPANY_LOCATION } from 'services/graphql/queries/location.query';
import { ILocation } from 'types/location';
import { CompanyRoleEnum, RoleEnum, UserStatusEnum } from 'types/user';
import { defaultNotification } from 'utils';

const NoLocationWarningModal: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { userFetchLoading, id, mainLocation, usersLocations, companyRole, status, role } = useCurrentLoggedInUser();
  const isClient = role === RoleEnum.CLIENT;
  const { content, loading: companyLoading } = useManyDataQuery<ILocation>(GET_COMPANY_LOCATION, {
    skip: role !== RoleEnum.CLIENT || companyRole !== CompanyRoleEnum.ADMIN,
  });

  const [warningModalVisibleForAdmin, setWarningModalVisibleForAdmin] = useState(true);

  const onOkClick = () => {
    history.push(ROUTES.COMPANY_LOCATION);
    setWarningModalVisibleForAdmin(false);
  };
  const isActive = status === UserStatusEnum.ACTIVE;
  const doesNeedLocationCreate =
    warningModalVisibleForAdmin &&
    !companyLoading &&
    (!content || !content.length) &&
    isActive &&
    !userFetchLoading &&
    companyRole === CompanyRoleEnum.ADMIN &&
    !usersLocations?.length;
  const doesNeedSetCurrentLocation =
    isActive &&
    !userFetchLoading &&
    companyRole === CompanyRoleEnum.REGULAR &&
    !mainLocation?.id &&
    !!usersLocations?.length;

  const onLogOutClick = () => {
    dispatch(AuthAction.userSignOut());
  };

  const [updateUser, { loading: updateUserLoading }] = useMutation(UPDATE_USER);
  const onMainLocationSelect = async (location: ILocation) => {
    if (mainLocation?.id === location.id) return;
    try {
      await updateUser({
        variables: {
          filter: {
            id,
          },
          updatedDoc: {
            mainLocation: location.id,
          },
        },
      });
      defaultNotification({ success: 'Update current Office Location successfully!' });
    } catch (err) {
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    }
  };

  return (
    <>
      <SweetAlertAsModal
        show={isClient && doesNeedLocationCreate}
        showCancel
        confirmBtnText='OK'
        // cancelBtnText='Log Me Out'
        onConfirm={onOkClick}
        // onCancel={onLogOutClick}
        focusConfirmBtn
        warning
        title='No Office Locations Exist, Please Create One or More Office Locations for Your Company'
      />
      <DefaultModal
        props={{
          title: 'Set Current Office Location',
          visible: isClient && doesNeedSetCurrentLocation,
          closable: false,
        }}>
        <div>
          <div className='p-2 text-sm font-medium'>You must set your current working location before continue!</div>
          <LocationSelectForm
            locations={usersLocations?.map((uL) => uL.location)}
            onLocationSelect={onMainLocationSelect}
            loading={updateUserLoading}
          />
          <div className='flex justify-center mt-2'>
            <CancelButton onClick={onLogOutClick}>Log Me Out</CancelButton>
          </div>
        </div>
      </DefaultModal>
    </>
  );
};

export default React.memo(NoLocationWarningModal);
